import axios from 'axios'
import { defineStore } from 'pinia'

const api = '/api/v1/holidays-public/support-requests'
const errorMessage = error => Object.values(error.response?.data?.errors ?? {}).flat()[0] ?? error.response?.data?.message ?? 'Unable to load support requests. Please try again.'

export const useSupportRequestStore = defineStore('holidaysPublicSupportRequests', {
  state: () => ({
    items: [],
    selected: null,
    options: { categories: {}, statuses: [], company: null },
    pagination: { currentPage: 1, lastPage: 1, total: 0 },
    isLoading: false,
    isSaving: false,
    error: null,
    creationError: null,
    listRequestId: 0,
    detailRequestId: 0,
  }),

  actions: {
    async fetchList({ companyUuid, status, page = 1 } = {}) {
      const requestId = ++this.listRequestId
      this.isLoading = true
      this.error = null
      this.items = []
      this.options = { categories: {}, statuses: [], company: null }

      try {
        const params = { company_uuid: companyUuid || undefined, status: status || undefined, page }
        const [list, options] = await Promise.all([axios.get(api, { params }), axios.get(`${api}/options`, { params: { company_uuid: params.company_uuid } })])
        if (requestId !== this.listRequestId) {
          return
        }
        this.items = list.data.data
        this.options = options.data.data
        this.pagination = { currentPage: list.data.meta.current_page, lastPage: list.data.meta.last_page, total: list.data.meta.total }
      } catch (error) {
        if (requestId === this.listRequestId) {
          this.error = errorMessage(error)
        }
      } finally {
        if (requestId === this.listRequestId) {
          this.isLoading = false
        }
      }
    },

    async fetchRequest(id) {
      const requestId = ++this.detailRequestId
      this.isLoading = true
      this.error = null
      this.selected = null

      try {
        const { data } = await axios.get(`${api}/${id}`)
        if (requestId === this.detailRequestId) {
          this.selected = data.data
        }
      } catch (error) {
        if (requestId === this.detailRequestId) {
          this.error = errorMessage(error)
        }
      } finally {
        if (requestId === this.detailRequestId) {
          this.isLoading = false
        }
      }
    },

    async createRequest(payload, company = null) {
      if (this.isSaving) {
        return null
      }

      if (!company?.id) {
        this.creationError = 'Choose a company before opening a support request.'
        return null
      }

      this.isSaving = true
      this.creationError = null

      try {
        const { data } = await axios.post(api, { subject: payload.subject, category: payload.category, description: payload.description, company_id: company.id })
        return data.data
      } catch (error) {
        this.creationError = errorMessage(error)
        return null
      } finally {
        this.isSaving = false
      }
    },

    async updateStatus(status) {
      if (!this.selected || this.isSaving) {
        return
      }

      const id = this.selected.id
      this.isSaving = true
      this.error = null

      try {
        const { data } = await axios.patch(`${api}/${id}`, { status })
        if (this.selected?.id === id) {
          this.selected = data.data
        }
      } catch (error) {
        this.error = errorMessage(error)
      } finally {
        this.isSaving = false
      }
    },

    async refreshRequest() {
      if (!this.selected) {
        return
      }

      const id = this.selected.id
      try {
        const { data } = await axios.get(`${api}/${id}`)
        if (this.selected?.id === id) {
          this.selected = data.data
        }
      } catch (error) {
        this.error = errorMessage(error)
      }
    },
  },
})
